"use client";

import { Breadcrumb } from "@pamperedchef/design-system";
import type { ComponentProps } from "react";
import * as styles from "./Breadcrumbs.module.scss";

export interface BreadcrumbItem {
	label: string;
	/** Omit on the last (current) entry — renders as plain text. */
	href?: string;
}

export interface BreadcrumbsProps {
	items: BreadcrumbItem[];
	/**
	 * When true, DS Breadcrumb renders a leading "← Back" affordance whose
	 * click invokes `history.back()`. Callers gate this on whether the user
	 * has prior in-app navigation in this tab.
	 */
	showBack?: boolean;
	className?: string;
}

// DS .d.ts is stale — the shipped `Breadcrumb` accepts `backButtonUrl` (any
// truthy string toggles the Back affordance). Cast to expose it locally.
type DsBreadcrumbProps = ComponentProps<typeof Breadcrumb> & {
	backButtonUrl?: string;
};
const DsBreadcrumb = Breadcrumb as React.ComponentType<DsBreadcrumbProps>;

/**
 * Generic breadcrumb chain. Caller assembles the chain (Home → category
 * crumbs → current entity) and passes the trailing entry without `href` so
 * DS renders it as the current page.
 *
 * Domain-agnostic on purpose: PDP, RDP, Category, and other surfaces each
 * own a tiny `build-breadcrumb-items.ts` that maps their entity →
 * `BreadcrumbItem[]`.
 */
export function Breadcrumbs({
	items,
	showBack,
	className
}: BreadcrumbsProps): JSX.Element | null {
	if (!items.length) return null;

	const dsItems = items.map((item) =>
		item.href
			? { href: item.href, children: item.label }
			: { children: item.label }
	);

	// DS Breadcrumb spreads its internal `Breadcrumb-module__breadcrumbs___…`
	// class BEFORE `...props`, so any `className` passed in REPLACES it.
	// Wrap in a div + own the wrapper's class so DS's spacing survives.
	const wrapperClass = [styles["wrapper"], className ?? ""]
		.filter(Boolean)
		.join(" ");

	return (
		<div className={wrapperClass} data-component="Breadcrumbs">
			<DsBreadcrumb
				aria-label="Breadcrumb"
				items={dsItems}
				backButtonUrl={showBack ? "back" : undefined}
			/>
		</div>
	);
}
